/**
 * Player module for Vuex store
 */
import AudioService from '@/services/AudioService';

export default {
  namespaced: true,
  
  state: () => ({
    currentTrackIndex: 0,
    currentTrackDetails: null,
    // Playback state is a MIRROR of AudioService, which derives it from the
    // audio element's own events (play/pause/waiting/ended...) and writes it
    // through SET_PLAYBACK_STATE. Nothing else should write these two:
    //   isPlaying      — the transport is on (audio playing, or about to)
    //   isTrackLoading — transport on but audio not flowing yet (fetching,
    //                    buffering, a play() still settling)
    // The play/pause button shows: spinner if isTrackLoading, else "pause"
    // if isPlaying, else "play".
    isPlaying: false,
    isShuffleMode: false,
    repeatMode: 0, // 0: off, 1: playlist, 2: track
    isUserHoldingProgressSlider: false,
    isTrackLoading: false,
  }),
  
  getters: {
    currentTrackIndex: state => state.currentTrackIndex,
    currentTrackDetails: state => state.currentTrackDetails,
    isPlaying: state => state.isPlaying,
    isShuffleMode: state => state.isShuffleMode,
    repeatMode: state => state.repeatMode,
    isUserHoldingProgressSlider: state => state.isUserHoldingProgressSlider,
    
    // Return the track URL based on the current track and audio quality setting
    currentTrackURL: (state, getters, rootState, rootGetters) => {
      if (!state.currentTrackDetails || !state.currentTrackDetails.name) {
        return null;
      }

      const isHighQuality = rootGetters['ui/isHighQualityAudioMode'] || false;
      const fileExtension = isHighQuality ? 'wav' : 'mp3';

      // Make sure the URL is defined
      const baseUrl = process.env.VUE_APP_RECORDINGS_URL || '';
      if (!baseUrl) {
        console.error('VUE_APP_RECORDINGS_URL is not defined');
        return null;
      }

      return baseUrl + formatURLString(state.currentTrackDetails.name) + '.' + fileExtension;
    },
    
    isTrackLoading: (state) => state.isTrackLoading,
    
    // Repeat icon based on repeat mode
    repeatIcon: state => {
      return state.repeatMode < 2 ? 'mdi-repeat' : 'mdi-repeat-once';
    },
  },
  
  mutations: {
    SET_CURRENT_TRACK_INDEX(state, index) {
      state.currentTrackIndex = index;
    },
    SET_CURRENT_TRACK_DETAILS(state, details) {
      state.currentTrackDetails = details;
    },
    SET_IS_PLAYING(state, isPlaying) {
      state.isPlaying = isPlaying;
    },
    // Written by AudioService only (see state comment)
    SET_PLAYBACK_STATE(state, { isPlaying, isLoading }) {
      state.isPlaying = !!isPlaying;
      state.isTrackLoading = !!isLoading;
    },
    SET_SHUFFLE_MODE(state, isShuffleMode) {
      state.isShuffleMode = isShuffleMode;
    },
    SET_REPEAT_MODE(state, repeatMode) {
      state.repeatMode = repeatMode;
    },
    SET_IS_USER_HOLDING_PROGRESS_SLIDER(state, isHolding) {
      state.isUserHoldingProgressSlider = isHolding;
    },
  },
  
  actions: {
    initializePlayer({ state, commit, dispatch, rootGetters }) {
      const recordings = rootGetters['playlist/sortedRecordings'];
      if (!recordings || recordings.length === 0) return;

      // If a track is already loaded, try to find it in the new recordings
      // (preserves audio continuity across login when playlist changes)
      if (state.currentTrackDetails) {
        const currentName = state.currentTrackDetails.name;
        const matchIndex = recordings.findIndex(r => r.name === currentName);

        if (matchIndex !== -1) {
          commit('SET_CURRENT_TRACK_INDEX', matchIndex);
          commit('SET_CURRENT_TRACK_DETAILS', recordings[matchIndex]);
          AudioService.updateMediaSessionMetadata(recordings[matchIndex]);
          AudioService.setTrackEndedCallback(() => dispatch('handleTrackEnded'));
          dispatch('uncollapseCurrentTrackYear');
          return;
        }
      }

      // No match or no current track — normal initialization
      commit('SET_CURRENT_TRACK_DETAILS', recordings[0]);
      commit('SET_CURRENT_TRACK_INDEX', 0);
      AudioService.updateMediaSessionMetadata(recordings[0]);
      AudioService.setTrackEndedCallback(() => dispatch('handleTrackEnded'));
      dispatch('loadTrack');
    },
    
    async loadTrack({ getters, dispatch }) {
      if (!getters.currentTrackURL) {
        return;
      }

      await AudioService.loadTrack(getters.currentTrackURL);
      dispatch('uncollapseCurrentTrackYear');
    },
    
    // NOTE: play/pause actions do not commit isPlaying themselves —
    // AudioService publishes the state the element actually reaches.
    async playTrack({ dispatch }) {
      const success = await AudioService.play();
      if (success) {
        dispatch('prefetchNextTrack');
      }
    },
    
    async pauseTrack() {
      // Use short fade to avoid click when user pauses
      await AudioService.fadeOutAndPause(100);
    },

    async pauseTrackWithFade(context, fadeDuration = 200) {
      await AudioService.fadeOutAndPause(fadeDuration);
    },
    
    togglePlayPause({ getters, dispatch }) {
      if (getters.isPlaying) {
        dispatch('pauseTrack');
      } else {
        dispatch('playTrack');
      }
    },
    
    seekToPosition({ commit }, positionPercent) {
      commit('SET_IS_USER_HOLDING_PROGRESS_SLIDER', true);
      AudioService.seekWithFade(positionPercent, 30);
      commit('SET_IS_USER_HOLDING_PROGRESS_SLIDER', false);
    },
    
    previousTrack({ getters, dispatch, rootGetters }) {
      const currentPosition = AudioService.getTrackProgress().currentTime;
      
      // If we're more than 3 seconds into the track, restart it instead
      if (getters.isPlaying && currentPosition >= 3) {
        AudioService.seekWithFade(0, 30);
        return;
      }
      
      const recordings = rootGetters['playlist/sortedRecordings'];
      let newIndex;
      
      if (getters.isShuffleMode) {
        newIndex = getRandomTrackIndex(0, recordings.length - 1, getters.currentTrackIndex);
      } else if (getters.currentTrackIndex === 0) {
        newIndex = recordings.length - 1;
      } else {
        newIndex = getters.currentTrackIndex - 1;
      }
      
      dispatch('changeTrack', newIndex);
    },
    
    nextTrack({ getters, dispatch, rootGetters }) {
      const recordings = rootGetters['playlist/sortedRecordings'];
      let newIndex;
      
      if (getters.isShuffleMode) {
        newIndex = getRandomTrackIndex(0, recordings.length - 1, getters.currentTrackIndex);
      } else if (getters.currentTrackIndex === recordings.length - 1) {
        newIndex = 0;
      } else {
        newIndex = getters.currentTrackIndex + 1;
      }
      
      dispatch('changeTrack', newIndex);
    },
    
    async changeTrack({ commit, dispatch, rootGetters }, newIndex) {
      const recordings = rootGetters['playlist/sortedRecordings'];

      if (newIndex >= 0 && newIndex < recordings.length) {
        commit('SET_CURRENT_TRACK_INDEX', newIndex);
        commit('SET_CURRENT_TRACK_DETAILS', recordings[newIndex]);

        // Update Media Session metadata with new track info
        AudioService.updateMediaSessionMetadata(recordings[newIndex]);

        // Transport on for the switch: the button shows the spinner while
        // the new track loads (loadTrack fades the old one out and masks
        // its own pause), then "pause" once audio is actually flowing.
        AudioService.beginTrackChange();
        await dispatch('loadTrack');

        // Play immediately after load (no setTimeout to preserve user gesture)
        dispatch('playTrack');
        // Prefetch the next track while this one plays (for iOS background playback)
        dispatch('prefetchNextTrack');
      }
    },
    
    selectTrack({ getters, dispatch }, { trackID, index }) {
      // If the currentTrackDetails is null or undefined, just change to the new track
      if (!getters.currentTrackDetails) {
        dispatch('changeTrack', index);
        return;
      }

      // If the same track was clicked, restart it
      if (getters.currentTrackDetails.id === trackID) {
        AudioService.seekWithFade(0, 30);
        return;
      }

      dispatch('changeTrack', index);
    },
    
    toggleShuffle({ commit, getters, dispatch }) {
      const newState = !getters.isShuffleMode;
      commit('SET_SHUFFLE_MODE', newState);
      
      // If repeat-one is on, turn it off (they're usually exclusive)
      if (newState && getters.repeatMode === 2) {
        dispatch('toggleRepeat');
      }
    },
    
    toggleRepeat({ commit, getters }) {
      let newMode;
      
      if (getters.repeatMode === 0) {
        newMode = 1; // Repeat playlist
      } else if (getters.repeatMode === 1) {
        newMode = 2; // Repeat track
      } else {
        newMode = 0; // No repeat
      }
      
      commit('SET_REPEAT_MODE', newMode);
    },
    
    handleTrackEnded({ getters, commit, dispatch, rootGetters }) {
      if (!getters.isPlaying) {
        return;
      }

      const recordings = rootGetters['playlist/sortedRecordings'];

      if (getters.repeatMode === 2) {
        // Repeat the current track — use immediate play for background safety
        AudioService.loadAndPlayImmediate(getters.currentTrackURL);
        return;
      }

      if (getters.currentTrackIndex === recordings.length - 1 && getters.repeatMode === 0) {
        // End of playlist and no repeat: transport off (the element is
        // already paused at its end) — the button shows "play"
        AudioService.stop();
        return;
      }

      // Determine the next track index
      let newIndex;
      if (getters.isShuffleMode) {
        newIndex = getRandomTrackIndex(0, recordings.length - 1, getters.currentTrackIndex);
      } else if (getters.currentTrackIndex === recordings.length - 1) {
        newIndex = 0; // repeat-all: wrap to first
      } else {
        newIndex = getters.currentTrackIndex + 1;
      }

      const nextTrack = recordings[newIndex];
      if (!nextTrack || !nextTrack.name) {
        AudioService.stop();
        return;
      }

      // Compute URL synchronously (same logic as currentTrackURL getter)
      const isHighQuality = rootGetters['ui/isHighQualityAudioMode'] || false;
      const ext = isHighQuality ? 'wav' : 'mp3';
      const baseUrl = process.env.VUE_APP_RECORDINGS_URL || '';
      if (!baseUrl) return;
      const nextUrl = baseUrl + formatURLString(nextTrack.name) + '.' + ext;

      // FAST PATH: synchronous src change + play() to prevent iOS
      // from suspending the web process. No await, no rAF, no fade —
      // the track already ended so there's nothing to fade.
      // (Publishes isPlaying itself; a play() that never starts heals it.)
      AudioService.loadAndPlayImmediate(nextUrl);

      // Update state with synchronous commits
      commit('SET_CURRENT_TRACK_INDEX', newIndex);
      commit('SET_CURRENT_TRACK_DETAILS', nextTrack);
      AudioService.updateMediaSessionMetadata(nextTrack);

      // Prefetch the track after this one
      dispatch('prefetchNextTrack');
    },
    
    /**
     * Prefetch the next track's audio data as a blob while the current
     * track is playing. iOS Safari blocks network requests when the
     * screen is locked, so having the next track in memory ensures
     * seamless background track advancement.
     */
    prefetchNextTrack({ getters, rootGetters }) {
      const recordings = rootGetters['playlist/sortedRecordings'];
      if (!recordings || recordings.length <= 1) return;

      // Can't predict next track in shuffle mode
      if (getters.isShuffleMode) return;
      // Repeat-one replays the same track — already loaded
      if (getters.repeatMode === 2) return;

      let nextIndex;
      if (getters.currentTrackIndex === recordings.length - 1) {
        if (getters.repeatMode === 1) {
          nextIndex = 0; // repeat-all: wrap to first
        } else {
          return; // end of playlist, no next track
        }
      } else {
        nextIndex = getters.currentTrackIndex + 1;
      }

      const nextTrack = recordings[nextIndex];
      if (!nextTrack || !nextTrack.name) return;

      const baseUrl = process.env.VUE_APP_RECORDINGS_URL || '';
      if (!baseUrl) return;

      // ALWAYS prefetch the MP3 variant — even in hi-fi (WAV) mode.
      // The prefetched blob is what makes locked-screen track advancement
      // work on iOS (network is blocked while locked), and a WAV blob would
      // hold 60-200 MB in memory on a low-RAM iPhone. In hi-fi mode the mp3
      // blob acts as an emergency fallback: if the WAV can't be fetched at
      // the moment the track ends, playback continues from the mp3 instead
      // of stopping (see AudioService._getEffectiveUrl).
      const nextUrl = baseUrl + formatURLString(nextTrack.name) + '.mp3';
      AudioService.prefetchTrack(nextUrl);
    },

    uncollapseCurrentTrackYear({ getters, dispatch, rootGetters }) {
      if (!getters.currentTrackDetails) return;
      
      const year = getters.currentTrackDetails.date.slice(0, 4);
      const yearsCollapsed = rootGetters['ui/yearsCollapsed'];
      
      if (yearsCollapsed.includes(year)) {
        dispatch('ui/toggleYearCollapse', year, { root: true });
      }
    },
    
    monitorTrackPlayback({ dispatch }) {
      AudioService.monitorTrackPlayback(
        // Retry callback
        () => {
          dispatch('loadTrack').then(() => {
            dispatch('playTrack');
          });
        },
        // Next track callback
        () => {
          dispatch('nextTrack');
        }
      );
    },

    /**
     * User started dragging progress slider
     */
    startHoldingProgressSlider({ commit }) {
      commit('SET_IS_USER_HOLDING_PROGRESS_SLIDER', true);
    },

    /**
     * User stopped dragging progress slider
     */
    stopHoldingProgressSlider({ commit }) {
      commit('SET_IS_USER_HOLDING_PROGRESS_SLIDER', false);
    },
  }
};

/**
 * Format a string for URL use
 * @param {string} str - The string to format
 * @returns {string} - The formatted string
 */
function formatURLString(str) {
  return str.replace(/ /g, "%20").replace(/\?/g, "%3F");
}

/**
 * Get a random track index different from the current index
 * @param {number} min - Minimum index
 * @param {number} max - Maximum index
 * @param {number} currentIndex - Current index to avoid
 * @returns {number} - Random index
 */
function getRandomTrackIndex(min, max, currentIndex) {
  min = Math.ceil(min);
  max = Math.floor(max);
  let randomInt = Math.floor(Math.random() * (max - min + 1) + min);
  
  // Make sure we don't return the current index
  while (randomInt === currentIndex) {
    randomInt = Math.floor(Math.random() * (max - min + 1) + min);
  }
  
  return randomInt;
}